/**
 * StripeCheckoutButton.js
 * 
 * Drop-in CTA button for PricingPage and platform subscription cards.
 * Uses website architectural fine-line design system with square sharp styling and #4D4D4D theme.
 */
import React, { useState } from 'react';
import { CreditCard, Loader2, ExternalLink } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.forwardsflow.com/api/v1';

const StripeCheckoutButton = ({ 
    planType, 
    billingInterval = 'monthly', 
    label = 'Subscribe Now',
    isEnterprise = false,
    className = '',
    popular = false 
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleCheckout = async () => {
        setError(null);

        // Enterprise -> contact sales
        if (isEnterprise) {
            window.location.href = 'mailto:sales@forwardsflow.com?subject=Enterprise Plan Inquiry';
            return;
        }

        // Free tier / trial -> register
        if (!planType) {
            window.location.href = '/register';
            return;
        }

        setLoading(true);

        try {
            const token = localStorage.getItem('authToken');
            const headers = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = 'Bearer ' + token;
            }

            const response = await fetch(API_BASE + '/stripe/create-checkout-session', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    planType,
                    billingInterval,
                    successUrl: window.location.origin + '/dashboard/billing?session_id={CHECKOUT_SESSION_ID}&status=success',
                    cancelUrl: window.location.origin + '/pricing?canceled=true'
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to create checkout session');
            }

            if (data.url) {
                window.location.href = data.url;
            } else {
                throw new Error('No checkout URL returned');
            }
        } catch (err) {
            console.error('Checkout error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full mt-auto pt-4">
            <button
                onClick={handleCheckout}
                disabled={loading}
                className={`w-full py-3.5 px-4 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${className}`}
            >
                {loading ? (
                    <>
                        <Loader2 className="w-4 h-4 animate-spin text-white" />
                        Redirecting to checkout...
                    </>
                ) : isEnterprise ? (
                    <>
                        Contact Sales <ExternalLink className="w-4 h-4" />
                    </>
                ) : (
                    <>
                        <CreditCard className="w-4 h-4" />
                        {label}
                    </>
                )}
            </button>
            {error && (
                <p className="mt-2 text-xs font-mono text-red-600 text-center">{error}</p>
            )}
        </div>
    );
};

export default StripeCheckoutButton;
